
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { History, TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/utils';

interface AuditLogEntry {
  id: string;
  change_trigger: string;
  created_at: string;
  previous_base_cost: number;
  previous_markup_percentage: number;
  previous_markup_source: string;
  previous_final_price: number;
  new_base_cost: number;
  new_markup_percentage: number;
  new_markup_source: string;
  new_final_price: number;
}

interface PricingAuditLogProps {
  lineItemId: string;
}

const PricingAuditLog: React.FC<PricingAuditLogProps> = ({ lineItemId }) => {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAuditLogs();
    }
  }, [isOpen, lineItemId]);

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('internal_line_item_pricing_audit')
        .select('*')
        .eq('line_item_id', lineItemId)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setAuditLogs(data || []);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPercentage = (percent: number) => `${percent.toFixed(2)}%`;

  const getChangeIcon = (oldValue: number, newValue: number) => {
    if (newValue > oldValue) return <TrendingUp className="h-3 w-3 text-green-600" />;
    if (newValue < oldValue) return <TrendingDown className="h-3 w-3 text-red-600" />;
    return null;
  };

  const getChangeColor = (oldValue: number, newValue: number) => {
    if (newValue > oldValue) return 'text-green-600';
    if (newValue < oldValue) return 'text-red-600';
    return 'text-gray-600';
  };

  const getTriggerBadgeVariant = (trigger: string) => {
    switch (trigger) {
      case 'manual_refresh': return 'default';
      case 'category_changed': return 'secondary';
      case 'cost_updated': return 'outline';
      case 'tier_modified': return 'destructive';
      default: return 'default';
    }
  };

  const getTriggerLabel = (trigger: string) => {
    switch (trigger) {
      case 'manual_refresh': return 'Manual Refresh';
      case 'category_changed': return 'Category Changed';
      case 'cost_updated': return 'Cost Updated';
      case 'tier_modified': return 'Tier Modified';
      default: return trigger;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <History className="h-4 w-4" />
          Pricing History
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pricing Change History</DialogTitle>
          <DialogDescription>
            Track all pricing changes for this line item over time.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-4">Loading audit logs...</div>
        ) : auditLogs.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No pricing changes recorded for this line item.
          </div>
        ) : (
          <div className="space-y-4">
            {auditLogs.map((log) => (
              <div key={log.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Badge variant={getTriggerBadgeVariant(log.change_trigger)}>
                    {getTriggerLabel(log.change_trigger)}
                  </Badge>
                  <span className="text-sm text-gray-500">
                    {format(new Date(log.created_at), 'dd MMMM yyyy HH:mm')}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Base Cost */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Base Cost</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{formatCurrency(log.previous_base_cost)}</span>
                      <span>→</span>
                      <span className={`text-sm ${getChangeColor(log.previous_base_cost, log.new_base_cost)}`}>
                        {formatCurrency(log.new_base_cost)}
                      </span>
                      {getChangeIcon(log.previous_base_cost, log.new_base_cost)}
                    </div>
                  </div>

                  {/* Markup */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Markup</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{formatPercentage(log.previous_markup_percentage)}</span>
                      <span>→</span>
                      <span className={`text-sm ${getChangeColor(log.previous_markup_percentage, log.new_markup_percentage)}`}>
                        {formatPercentage(log.new_markup_percentage)}
                      </span>
                      {getChangeIcon(log.previous_markup_percentage, log.new_markup_percentage)}
                    </div>
                  </div>

                  {/* Source */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Source</div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {log.previous_markup_source}
                      </Badge>
                      <span>→</span>
                      <Badge variant="outline" className="text-xs">
                        {log.new_markup_source}
                      </Badge>
                    </div>
                  </div>

                  {/* Final Price */}
                  <div className="space-y-1">
                    <div className="text-sm font-medium">Final Price</div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{formatCurrency(log.previous_final_price)}</span>
                      <span>→</span>
                      <span className={`text-sm font-semibold ${getChangeColor(log.previous_final_price, log.new_final_price)}`}>
                        {formatCurrency(log.new_final_price)}
                      </span>
                      {getChangeIcon(log.previous_final_price, log.new_final_price)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PricingAuditLog;
